import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MarineOrganizationAccess } from './AuthGate';
import { supabase } from './lib/supabase';
import './brokerage-source-sync.css';

type SourceRow={
  id:string;
  name:string;
  index_url:string;
  active:boolean;
  sync_interval_minutes:number;
  last_attempt_at:string|null;
  last_success_at:string|null;
  last_status:string|null;
  last_summary:Record<string,any>|null;
};

type Props={organization:MarineOrganizationAccess;onSynced:()=>void};

export default function BrokerageWebsiteSyncBridge({organization,onSynced}:Props){
  const [host,setHost]=useState<HTMLElement|null>(null);
  const [source,setSource]=useState<SourceRow|null>(null);
  const [canSync,setCanSync]=useState(organization.roles.includes('platform_admin'));
  const [phase,setPhase]=useState<'idle'|'listings'|'photos'>('idle');
  const [message,setMessage]=useState('');
  const autoStarted=useRef(false);
  const photoResumeStarted=useRef(false);

  useEffect(()=>{
    const bind=()=>{
      const target=(document.querySelector('.listing-heading-actions')||document.querySelector('.hero-actions')) as HTMLElement|null;
      if(target!==host)setHost(target);
    };
    bind();
    const observer=new MutationObserver(bind);
    observer.observe(document.body,{childList:true,subtree:true});
    return()=>observer.disconnect();
  },[host]);

  useEffect(()=>{
    let cancelled=false;
    async function loadSource(){
      if(!supabase)return;
      if(!organization.roles.includes('platform_admin')){
        const {data:user}=await supabase.auth.getUser();
        const uid=user.user?.id;
        if(uid){
          const {data:membership}=await supabase.from('marine_organization_members').select('role,active').eq('organization_id',organization.id).eq('user_id',uid).maybeSingle();
          if(!cancelled)setCanSync(Boolean(membership?.active&&['owner_admin','broker'].includes(membership.role||'')));
        }
      }
      const {data}=await supabase.from('marine_brokerage_listing_sources').select('id,name,index_url,active,sync_interval_minutes,last_attempt_at,last_success_at,last_status,last_summary').eq('organization_id',organization.id).eq('active',true).eq('source_type','vyg_website').maybeSingle();
      if(!cancelled)setSource((data as SourceRow|null)||null);
    }
    loadSource();
    return()=>{cancelled=true};
  },[organization.id]);

  useEffect(()=>{
    if(!source||!canSync||autoStarted.current||phase!=='idle')return;
    autoStarted.current=true;
    const last=source.last_success_at?new Date(source.last_success_at).getTime():0;
    const interval=Math.max(15,Number(source.sync_interval_minutes||360))*60_000;
    if(!last||Date.now()-last>=interval)void runSync(false);
  },[source?.id,canSync]);

  useEffect(()=>{
    if(!source||!canSync||photoResumeStarted.current||phase!=='idle'||!supabase)return;
    const last=source.last_success_at?new Date(source.last_success_at).getTime():0;
    const interval=Math.max(15,Number(source.sync_interval_minutes||360))*60_000;

    // If the listing feed itself is due, runSync will also start the photo
    // worker. Otherwise, independently resume any gallery work left behind by
    // an interrupted/older sync without refreshing the listings again.
    if(!last||Date.now()-last>=interval)return;
    photoResumeStarted.current=true;
    void (async()=>{
      const {data,error}=await supabase!.from('marine_vessels').select('id,specifications').eq('organization_id',organization.id);
      if(error)return;
      const pending=(data||[]).some((row:any)=>{
        const specs=row.specifications||{};
        return String(specs.source_system||'').toLowerCase()==='vancouver_yacht_group_website' &&
          (Number(specs.source_media_sync_version||0)<6 || !specs.source_media_sync_completed_at);
      });
      if(pending)await syncPhotos();
    })();
  },[source?.id,canSync]);

  async function syncPhotos(){
    if(!supabase)return;
    setPhase('photos');
    let remaining=1;
    let rounds=0;
    let processed=0;

    // A full VYG inventory can exceed forty listings. Give the background
    // gallery worker enough bounded passes to finish the complete feed without
    // making the brokerage workspace wait on photo downloads.
    while(remaining>0&&rounds<12){
      const {data,error}=await supabase.functions.invoke('marine-sync-source-media',{body:{organization_id:organization.id,batch_size:6}});
      if(error||data?.error)break;
      processed+=Number(data?.processed||0);
      remaining=Number(data?.remaining||0);
      rounds++;
      if(remaining>0)await new Promise(resolve=>setTimeout(resolve,500));
    }
    if(processed>0)onSynced();
    if(remaining>0)setMessage(prev=>prev?`${prev} · ${remaining} photo batch item${remaining===1?'':'s'} will retry in background`:`${remaining} photo batch item${remaining===1?'':'s'} will retry in background`);
    setPhase('idle');
  }

  async function runSync(force:boolean){
    if(!supabase||!source||phase!=='idle')return;
    setMessage('');
    setPhase('listings');
    const {data,error}=await supabase.functions.invoke('marine-sync-listing-source',{body:{organization_id:organization.id,source_id:source.id,force}});
    if(error||data?.error){
      setMessage(error?.message||String(data?.error||'Listing sync could not be completed.'));
      setPhase('idle');
      return;
    }
    if(data?.skipped){
      setMessage('Listings are already up to date.');
      setPhase('idle');
      return;
    }
    const summary=data?.summary||{};
    const created=Number(summary.created_count||0);
    const updated=Number(summary.updated_count||0);
    const matched=Number(summary.matched_existing_count||0);
    const sold=Number(summary.sold_skipped_count||0);
    setSource(prev=>prev?{...prev,last_success_at:new Date().toISOString(),last_status:'success',last_summary:summary}:prev);
    setMessage(`${Number(summary.source_inventory_count||0)} active website listings checked · ${created} added · ${updated} refreshed${matched?` · ${matched} matched`:''}${sold?` · ${sold} sold excluded`:''}`);
    onSynced();
    void syncPhotos();
  }

  if(!source||!canSync)return null;
  const button=host?createPortal(<button type="button" className="secondary-button compact brokerage-sync-button" disabled={phase!=='idle'} onClick={()=>runSync(true)} title="Refresh active inventory from the Vancouver Yacht Group website">{phase==='listings'?'Syncing listings…':phase==='photos'?'Syncing photos…':'Sync website'}</button>,host):null;
  return <>{button}{message&&<div className="brokerage-sync-toast" role="status"><strong>Website inventory</strong><span>{message}</span><button type="button" onClick={()=>setMessage('')} aria-label="Dismiss">×</button></div>}</>;
}
